// Orchestrator for the Olive newsletter builder.
//
// Owns the section list state, top-level effects (loading custom images,
// keeping the parent's HTML/sections in sync), and the per-section action
// handlers (update/colorize/delete + add news/case-study/news-item).
//
// The actual visual + HTML for each section type lives in sibling files:
//   - header-section.tsx         (HeaderSection + headerToHtml)
//   - introducing-section.tsx    (IntroducingSection + introducingToHtml)
//   - case-study-section.tsx     (CaseStudySection + caseStudyToHtml)
//   - company-news-section.tsx   (CompanyNewsSection + companyNewsToHtml)
//   - footer-section.tsx         (FooterSection + footerToHtml)
//   - stock-images.ts            (NewsletterSection/TenantInfo types,
//                                 stock image catalogues, toAbsoluteUrl)

import { useState, useEffect, useLayoutEffect, useRef } from "react"
import { Box, Button, Popover, Typography } from "@mui/material"
import { Plus } from "lucide-react"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { newsletterImagesApi } from "@crm-src/lib/newsletter-images-api"
import { useToast } from "@crm-src/hooks/use-toast"

import {
  NewsletterSection,
  TenantInfo,
  headerImageOptions,
  defaultNewsImages,
} from "./stock-images"
import { normalizeNewsletterSections } from "./normalize"
import { generateEmailHTML } from "./generate-email-html"
import { getCaseStudyDefaultSections } from "./case-study-template"
import { HeaderSection } from "./header-section"
import { IntroducingSection } from "./introducing-section"
import { CaseStudySection } from "./case-study-section"
import { CompanyNewsSection } from "./company-news-section"
import { FooterSection } from "./footer-section"

// Public prop API — unchanged from the pre-refactor monolithic component
// so the two existing call sites (email-campaigns/page.tsx and
// email-campaign-form/content-editor.tsx) keep working without churn.
interface OliveNewsletterBuilderProps {
  tenantName: string
  tenantLogo?: string | null
  tenantInfo?: TenantInfo
  initialSections?: NewsletterSection[]
  /** Which default section set to seed a NEW (unsaved) document with. The
   * "Newsletter" and "Case Study" templates both ride this same
   * section builder — they differ only in their starting sections. Ignored
   * once `initialSections` (a saved campaign's content_data) is present. */
  preset?: 'newsletter' | 'case_study'
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: NewsletterSection[]) => void
}

export function OliveNewsletterBuilder({
  tenantName,
  tenantLogo,
  tenantInfo,
  initialSections,
  preset = 'newsletter',
  onContentChange,
  onSectionsChange,
}: OliveNewsletterBuilderProps) {
  const currentDate = new Date()
  const month = currentDate.toLocaleString('en-US', { month: 'long' })
  const year = currentDate.getFullYear()

  // Default sections — used when no `initialSections` are passed in. The
  // "Case Study" template seeds a different starting arrangement (title image
  // → customer → problem → solution → network diagram → benefits); the default
  // "Newsletter" is header → case-study → company-news → footer.
  const getDefaultSections = (): NewsletterSection[] =>
    preset === 'case_study' ? getCaseStudyDefaultSections() : [
    {
      id: 'header',
      type: 'header',
      content: {
        title: 'XOP Connect - Newsletter',
        subtitle: `${month} ${year}`,
        imageUrl: headerImageOptions[0].url
      }
    },
    {
      id: 'case-study',
      type: 'case_study',
      backgroundColor: '#ffffff',
      content: {
        heading: 'Success Story',
        title: 'How We Helped Transform Business Communications',
        content: '<p>We are thrilled to share the success story of our valued customer who recently implemented our conferencing solution. They experienced significant improvements in their business operations and customer satisfaction.</p>'
      }
    },
    {
      id: 'news',
      type: 'company_news',
      backgroundColor: '#f9fdf7',
      content: {
        heading: 'Company News',
        items: [
          {
            title: 'New Feature Release: Advanced Analytics Dashboard',
            text: 'We are excited to announce the launch of our new Advanced Analytics Dashboard, providing real-time insights into your business performance.',
            images: [defaultNewsImages[0]]
          },
          {
            title: 'Team Expansion & New Hires',
            text: 'Our team continues to grow! This month we welcomed 5 new talented professionals to our customer success and development teams.',
            images: [defaultNewsImages[1]]
          },
          {
            title: 'Upcoming Webinar: Maximizing CRM ROI',
            text: 'Join us for an exclusive webinar on March 15th at 2:00 PM EST where we will share best practices and strategies for maximizing your CRM investment.',
            images: [defaultNewsImages[2]]
          }
        ]
      }
    },
    {
      id: 'footer',
      type: 'footer',
      backgroundColor: '#2d3748',
      content: {}
    }
  ]

  const [sections, setSections] = useState<NewsletterSection[]>(
    initialSections && initialSections.length > 0
      ? normalizeNewsletterSections(initialSections)
      : getDefaultSections()
  )
  const [isUploadingHeaderImage, setIsUploadingHeaderImage] = useState(false)
  const [customHeaderImages, setCustomHeaderImages] = useState<Array<{ id: number, url: string, name: string }>>([])
  // Pending custom-image delete — child sections call onDeleteCustomImage
  // which now opens this confirmation instead of deleting immediately.
  const [pendingDeleteImage, setPendingDeleteImage] = useState<{ imageId: number; imageUrl: string } | null>(null)
  const headerImageInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Load custom uploaded images on mount
  useEffect(() => {
    const loadCustomImages = async () => {
      try {
        const images = await newsletterImagesApi.list()
        const formattedImages = images.map((img, index) => ({
          id: 1000 + index, // Use high IDs to avoid conflicts with stock images
          url: img.url,
          name: img.name || 'Custom Image'
        }))
        setCustomHeaderImages(formattedImages)
      } catch (error) {
        console.error('Failed to load custom images:', error)
      }
    }
    loadCustomImages()
  }, [])

  // Update sections when initialSections prop changes (e.g., when loading from saved campaign)
  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      // Normalize so a saved section missing a nested field (older shape)
      // can't crash a section renderer on edit. See ./normalize.ts (M9 fix).
      setSections(normalizeNewsletterSections(initialSections))
    }
  }, [initialSections])

  const [editingSection, setEditingSection] = useState<string | null>(null)
  const [addSectionAnchor, setAddSectionAnchor] = useState<null | HTMLElement>(null)

  // Track last sections and content to prevent infinite loops in the
  // useLayoutEffect that sync's HTML / sections back to the parent.
  const lastSectionsJsonRef = useRef<string>("")
  const lastContentHtmlRef = useRef<string>("")

  const updateSection = (sectionId: string, newContent: any) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, content: { ...section.content, ...newContent } }
        : section
    ))
  }

  const changeSectionColor = (sectionId: string, color: string) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, backgroundColor: color }
        : section
    ))
  }

  const deleteSection = (sectionId: string) => {
    setSections(sections.filter(section => section.id !== sectionId))
  }

  // Generate HTML for email from sections. The output is dispatched
  // through per-section `xxxToHtml` helpers so each section module owns
  // its own template — but the shell (DOCTYPE, outer table, etc.) and
  // the dispatch order live here.

  // Update parent component whenever sections change OR on mount.
  // useLayoutEffect ensures content is set synchronously before paint
  // — prevents "Content can't be blank" errors when saving immediately
  // after load. Refs short-circuit no-op updates to avoid render loops.
  useLayoutEffect(() => {
    const html = generateEmailHTML(sections, { tenantName, tenantLogo, tenantInfo, year })

    if (html !== lastContentHtmlRef.current) {
      lastContentHtmlRef.current = html
      onContentChange(html)
    }

    if (onSectionsChange) {
      const sectionsJson = JSON.stringify(sections)
      if (sectionsJson !== lastSectionsJsonRef.current) {
        lastSectionsJsonRef.current = sectionsJson
        onSectionsChange(sections)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections])

  // Adds a new news item to the (single) company_news section, picks a
  // default image from the rotation, and scrolls the new item into view
  // so the user can immediately edit it.
  const addNewsItem = () => {
    const newsSection = sections.find(s => s.type === 'company_news')
    if (newsSection) {
      const newIndex = newsSection.content.items.length
      const defaultImage = defaultNewsImages[newIndex % defaultNewsImages.length]
      updateSection(newsSection.id, {
        items: [
          ...newsSection.content.items,
          { title: 'New News Item', text: 'Add your news content here...', images: [defaultImage] }
        ]
      })

      // Scroll to the newly added news item after a short delay to allow rendering
      setTimeout(() => {
        setEditingSection(`${newsSection.id}-${newIndex}`)
        const newsItemElements = document.querySelectorAll('[data-news-item-index]')
        const newItemElement = Array.from(newsItemElements).find(
          el => el.getAttribute('data-news-item-index') === `${newsSection.id}-${newIndex}`
        )
        if (newItemElement) {
          newItemElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }

  // Validates and uploads a custom header image, then selects it on the
  // owning section. State for both `isUploadingHeaderImage` and
  // `customHeaderImages` is owned here and lifted into HeaderSection.
  const handleHeaderImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, sectionId: string) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPEG, PNG, GIF, or WebP)",
        variant: "destructive"
      })
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB",
        variant: "destructive"
      })
      return
    }

    try {
      setIsUploadingHeaderImage(true)
      const uploadedImage = await newsletterImagesApi.upload(file)

      const newCustomImage = {
        id: 1000 + customHeaderImages.length,
        url: uploadedImage.url,
        name: uploadedImage.name
      }
      setCustomHeaderImages(prev => [...prev, newCustomImage])

      updateSection(sectionId, { imageUrl: uploadedImage.url })

      toast({
        title: "Image uploaded",
        description: "Your custom header image has been uploaded successfully"
      })

      if (headerImageInputRef.current) {
        headerImageInputRef.current.value = ''
      }
    } catch (error: any) {
      console.error('Failed to upload header image:', error)
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image",
        variant: "destructive"
      })
    } finally {
      setIsUploadingHeaderImage(false)
    }
  }

  // Trash button on a custom image opens a confirmation. The actual
  // deletion runs in `confirmDeleteCustomImage` after the user confirms.
  const handleDeleteCustomImage = (imageId: number, imageUrl: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setPendingDeleteImage({ imageId, imageUrl })
  }

  // Deletes a user-uploaded custom image. If any header section was
  // currently pointing at that URL, fall back to the first stock image.
  const confirmDeleteCustomImage = async () => {
    if (!pendingDeleteImage) return
    const { imageId, imageUrl } = pendingDeleteImage

    try {
      // Round-trip via the API to find the real attachment ID — our
      // local `customHeaderImages` state assigns synthetic ids to avoid
      // collisions with stock-image numeric ids.
      const images = await newsletterImagesApi.list()
      const imageToDelete = images.find(img => img.url === imageUrl)

      if (!imageToDelete) {
        throw new Error('Image not found')
      }

      await newsletterImagesApi.delete(imageToDelete.id)

      setCustomHeaderImages(prev => prev.filter(img => img.id !== imageId))

      const updatedSections = sections.map(section => {
        if (section.type === 'header' && section.content.imageUrl === imageUrl) {
          return {
            ...section,
            content: {
              ...section.content,
              imageUrl: headerImageOptions[0].url
            }
          }
        }
        return section
      })
      setSections(updatedSections)

      toast({
        title: "Image deleted",
        description: "Your custom image has been deleted successfully"
      })
    } catch (error: any) {
      console.error('Failed to delete image:', error)
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image",
        variant: "destructive"
      })
      // Keep ConfirmDialog open so the user can retry / cancel.
      throw error
    }
  }

  // Inserts a new section just above the footer. Used by the "Add Section"
  // popover at the bottom of the visual builder.
  const addSection = (type: 'case_study' | 'company_news') => {
    const footerIndex = sections.findIndex(s => s.type === 'footer')
    const newSection: NewsletterSection = type === 'case_study' ? {
      id: `case-study-${Date.now()}`,
      type: 'case_study',
      backgroundColor: '#ffffff',
      content: {
        heading: 'Success Story',
        title: 'How We Helped Transform Their Business',
        content: '<p>Write your success story content here...</p>'
      }
    } : {
      id: `news-${Date.now()}`,
      type: 'company_news',
      backgroundColor: '#f9fdf7',
      content: {
        heading: 'Company News',
        items: [
          {
            title: 'News Title',
            text: 'News content goes here...',
            images: [defaultNewsImages[0]]
          }
        ]
      }
    }

    const newSections = [...sections]
    newSections.splice(footerIndex, 0, newSection)
    setSections(newSections)
  }

  // Dispatches a section to its owning component. The header has a
  // simple isEditing flag; company_news has additional sub-edit modes
  // (heading edit, per-item edit) keyed off `${section.id}-...`.
  const renderSection = (section: NewsletterSection) => {
    const isEditing = editingSection === section.id

    switch (section.type) {
      case 'header':
        return (
          <HeaderSection
            section={section}
            isEditing={isEditing}
            onUpdate={updateSection}
            setEditingSection={setEditingSection}
            tenantName={tenantName}
            tenantLogo={tenantLogo}
            tenantInfo={tenantInfo}
            customHeaderImages={customHeaderImages}
            isUploadingHeaderImage={isUploadingHeaderImage}
            headerImageInputRef={headerImageInputRef}
            onHeaderImageUpload={handleHeaderImageUpload}
            onDeleteCustomImage={handleDeleteCustomImage}
          />
        )

      case 'introducing':
        return (
          <IntroducingSection
            section={section}
            isEditing={isEditing}
            onUpdate={updateSection}
            onChangeColor={changeSectionColor}
            onDelete={deleteSection}
            setEditingSection={setEditingSection}
          />
        )

      case 'case_study':
        return (
          <CaseStudySection
            section={section}
            isEditing={isEditing}
            onUpdate={updateSection}
            onChangeColor={changeSectionColor}
            onDelete={deleteSection}
            setEditingSection={setEditingSection}
          />
        )

      case 'company_news':
        return (
          <CompanyNewsSection
            section={section}
            editingSection={editingSection}
            onUpdate={updateSection}
            onChangeColor={changeSectionColor}
            onDelete={deleteSection}
            setEditingSection={setEditingSection}
            onAddNewsItem={addNewsItem}
          />
        )

      case 'footer':
        return (
          <FooterSection
            section={section}
            tenantName={tenantName}
            tenantInfo={tenantInfo}
            year={year}
          />
        )

      default:
        return null
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Visual Email Builder */}
      <Box
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper", maxWidth: "800px", mx: "auto" }}
      >
        {sections.map((section, index) => (
          <div key={section.id}>
            {renderSection(section)}
            {/* Add Section button before footer — appears below the LAST
                content section, just above the footer */}
            {(section.type === 'company_news' || section.type === 'case_study') && sections[index + 1]?.type === 'footer' && (
              <Box sx={{ p: 2.5, textAlign: "center", borderTop: "2px dashed #e2e8f0" }}>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={(e) => setAddSectionAnchor(e.currentTarget)}
                  startIcon={<Plus size={16} />}
                >
                  Add Section
                </Button>
                <Popover
                  open={Boolean(addSectionAnchor)}
                  anchorEl={addSectionAnchor}
                  onClose={() => setAddSectionAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  slotProps={{ paper: { sx: { width: 256, p: 2 } } }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>Choose Section Type</Typography>
                    <Button
                      fullWidth
                      sx={{ justifyContent: "flex-start" }}
                      variant="outlined"
                      color="inherit"
                      onClick={() => { addSection('case_study'); setAddSectionAnchor(null) }}
                      startIcon={<Plus size={16} />}
                    >
                      Success Story
                    </Button>
                    <Button
                      fullWidth
                      sx={{ justifyContent: "flex-start" }}
                      variant="outlined"
                      color="inherit"
                      onClick={() => { addSection('company_news'); setAddSectionAnchor(null) }}
                      startIcon={<Plus size={16} />}
                    >
                      Company News
                    </Button>
                  </Box>
                </Popover>
              </Box>
            )}
          </div>
        ))}
      </Box>

      <ConfirmDialog
        isOpen={pendingDeleteImage !== null}
        onClose={() => setPendingDeleteImage(null)}
        title="Delete custom image?"
        description="This image will be removed from your library. Any header section using this image will fall back to a stock image. This cannot be undone."
        confirmLabel="Delete image"
        onConfirm={confirmDeleteCustomImage}
      />
    </Box>
  )
}
